import { Table, Thead, Tbody, Tr, Th, Td } from '@components/core-ui/Table/Table';
import { TagInfoRounded } from '@components/core-ui/Tag/variations/TagInfoRounded';
import { TagMutedRounded } from '@components/core-ui/Tag/variations/TagMutedRounded';
import { routes } from '@routes';
import { Link } from 'react-router-dom';
import type { PipelineAttempt } from '../types/dashboardTypes';
import { formatDateTime, formatDurationMs } from '../utils';
import { SectionPagination } from './SectionPagination';

type TProps = {
  attempts: PipelineAttempt[];
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  confirmMarkAttemptFailed: (attemptId: string) => void;
};

export const CurrentAttemptsCard = ({
  attempts,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  confirmMarkAttemptFailed,
}: TProps) => (
  <div className="bg-[var(--color-surface)] shadow-[0_8px_10px_var(--color-shadow)] rounded-[10px] p-5 mb-5 w-full">
    <div className="flex flex-wrap gap-2 items-center justify-between mb-4">
      <div className="text-[var(--color-text-muted)] font-medium text-lg leading-[150%]">
        Oldest active pipeline attempts
      </div>
      <div className="text-xs text-[var(--color-text-muted)]">
        Ordered by start time • {totalCount} attempt{totalCount === 1 ? '' : 's'}
      </div>
    </div>
    {attempts.length === 0 ? (
      <div className="p-2.5">No active attempts currently need attention.</div>
    ) : (
      <>
        <div className="overflow-x-auto">
          <Table className="w-full text-sm text-left text-[var(--color-text)]">
            <Thead className="text-xs uppercase text-[var(--color-text-muted)]">
              <Tr className="border-b border-border">
                <Th className="py-2 pr-2 whitespace-nowrap">Job</Th>
                <Th className="py-2 pr-2 whitespace-nowrap">Attempt</Th>
                <Th className="py-2 pr-2 whitespace-nowrap">Latest event</Th>
                <Th className="py-2 pr-2 whitespace-nowrap">Elapsed</Th>
                <Th className="py-2 pr-2 whitespace-nowrap">Started</Th>
                <Th className="py-2 pr-2 text-right whitespace-nowrap">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {attempts.map((attempt: PipelineAttempt) => (
                <Tr key={attempt.attemptId} className="border-b border-border last:border-0">
                  <Td className="py-3 pr-2 align-top">
                    {attempt.jobId ? (
                      <Link
                        to={routes.adminJobDetails.make(attempt.jobId)}
                        className="text-[var(--color-text)] font-medium hover:underline"
                      >
                        {attempt.jobName}
                      </Link>
                    ) : (
                      <div className="text-[var(--color-text)] font-medium">{attempt.jobName}</div>
                    )}
                    <div className="text-[var(--color-text-muted)] text-xs flex flex-wrap gap-1">
                      <span>Job ID:</span>
                      {attempt.jobId ? (
                        <Link
                          to={routes.adminJobDetails.make(attempt.jobId)}
                          className="text-[var(--color-link)] hover:underline"
                        >
                          {attempt.jobId}
                        </Link>
                      ) : (
                        <span>-</span>
                      )}
                      <span>• Status: {attempt.jobStatus}</span>
                    </div>
                  </Td>
                  <Td className="py-3 pr-2 align-top">
                    <div className="flex flex-wrap gap-1">
                      <TagInfoRounded className="inline-flex text-[11px] font-semibold uppercase whitespace-nowrap !px-2 !py-1">
                        {attempt.attemptStatus}
                      </TagInfoRounded>
                      {attempt.autoRetried ? (
                        <TagMutedRounded className="inline-flex text-[11px] font-semibold uppercase whitespace-nowrap !px-2 !py-1">
                          AUTO-RETRIED
                        </TagMutedRounded>
                      ) : null}
                    </div>
                  </Td>
                  <Td className="py-3 pr-2 align-top text-[var(--color-text)]">
                    <div className="text-sm whitespace-nowrap">{attempt.latestEventStage ?? '-'}</div>
                    <div className="text-xs text-[var(--color-text-muted)] whitespace-nowrap">
                      {attempt.latestEventStatus ?? '-'}
                    </div>
                  </Td>
                  <Td className="py-3 pr-2 align-top text-[var(--color-text)] whitespace-nowrap">
                    {formatDurationMs(attempt.durationMs)}
                  </Td>
                  <Td className="py-3 pr-2 align-top text-[var(--color-text-muted)] text-xs">
                    {formatDateTime(attempt.startedAt)}
                  </Td>
                  <Td className="py-3 pr-2 align-top text-right">
                    <div className="flex gap-2 flex-wrap justify-end">
                      <button
                        type="button"
                        onClick={() => confirmMarkAttemptFailed(attempt.attemptId)}
                        className="text-xs px-3 py-2 rounded-md border border-orange-500 text-orange-800 bg-orange-50 hover:bg-orange-100"
                      >
                        Mark failed
                      </button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
        <div className="mt-4">
          <SectionPagination
            totalCount={totalCount}
            page={page}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      </>
    )}
  </div>
);
